import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(user?.plan || "founders");
  const [busy, setBusy] = useState("");

  const load = async () => { const r = await api.billingPlans(); setPlans(r.plans || []); setCurrent(r.current); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("billing") === "success" && p.get("session_id")) {
      (async () => { try { await api.billingStatus(p.get("session_id")); toast.success("Plan activated — welcome aboard!"); await refresh(); await load(); } catch { /* ignore */ } })();
      window.history.replaceState(null, "", "/app/pricing");
    } else if (p.get("billing") === "cancel") { toast("Checkout cancelled."); window.history.replaceState(null, "", "/app/pricing"); }
  }, []);

  const choose = async (plan) => {
    if (plan.custom) { toast("Contact us at hello@indiewood.app for Civilization / enterprise."); return; }
    setBusy(plan.id);
    try {
      const r = await api.billingCheckout(plan.id, window.location.origin);
      if (r.free) { toast.success(`You're on the ${plan.name} plan`); await refresh(); await load(); }
      else if (r.checkout_url) { toast.success("Redirecting to secure checkout…"); window.location.href = r.checkout_url; }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-10" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="mono text-white/40">GROW THROUGH THE STAGES · ARTIST TO AUDIENCE</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Plans that scale with your audience</h1>
        <p className="text-white/60 mt-3">From your first three fans to a civilization of millions. Start free, upgrade as you grow. Prices in ₹ / month.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map(p => {
          const active = current === p.id;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`relative border rounded-xl p-6 flex flex-col ${active ? "border-kinetic bg-kinetic/5" : "border-white/10 bg-[#0C0C0C]"}`}>
              {active && <span className="absolute top-4 right-4 mono text-black bg-kinetic px-2 py-0.5 rounded-sm">CURRENT</span>}
              <div className="mono text-kinetic">{p.subs}</div>
              <h3 className="font-sans text-2xl font-extrabold mt-1">{p.name}</h3>
              <div className="mt-3">
                {p.custom ? <div className="text-2xl font-extrabold">Custom</div>
                  : p.price === 0 ? <div className="text-3xl font-extrabold">Free</div>
                  : <div className="text-3xl font-extrabold">₹{p.price.toLocaleString("en-IN")}<span className="text-white/40 text-base font-normal">/mo</span></div>}
                {p.tag && <div className="mono text-white/40 mt-1">{p.tag}</div>}
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/75"><Check className="w-4 h-4 text-kinetic shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button data-testid={`plan-choose-${p.id}`} onClick={() => choose(p)} disabled={busy === p.id || active}
                className={`mt-5 rounded-sm ${active ? "bg-white/10 text-white/60" : "kinetic-btn"}`}>
                {active ? "Current plan" : busy === p.id ? "…" : p.custom ? "Contact us" : p.price === 0 ? "Choose Free" : "Upgrade"}
              </Button>
            </div>
          );
        })}
      </div>
      <div className="text-center mono text-white/30 flex items-center justify-center gap-2"><Sparkles className="w-3 h-3" /> SECURE PAYMENTS · STRIPE · INR · CANCEL ANYTIME</div>
    </div>
  );
}
